/**
 * The people and companies that own the entity, for the tax year the page is showing.
 *
 * Expects the owners the api sent, one TaxProfileListRow each, the tax year the page shows, the
 * "Needs review only" filter, the page's writes, busy while a write is in flight, and the callback
 * that opens a source document.
 *
 * Renders the section heading and a ListTable of one row per owner: Name, Type, Owns, Profit, US
 * person and the Approval column. How a row is reviewed is ListTable's. The name opens ListTable's
 * person window (personDetails), which holds the cells the row does not draw: the taxpayer number
 * (owner_tin, masked to its last four digits), street address, city, state, ZIP code, capital share and beginning capital account. The US person cell draws an icon read off the owner_foreign_person cell's value: a
 * flag for "no", a globe for "yes", a question mark when nothing says either, each with its sentence
 * as the tooltip; the cell opens ValuePicker like the others, offering Yes and No.
 *
 * Under the owners a Total row sums Owns and Profit over every owner from the value each cell
 * shows (the approved value, or the choice made in the row, or the newest waiting value). A total
 * more than half a point away from 100 is drawn in the warning color with ", not 100%". The note
 * at the right says whether the totals count values still waiting for review. The Total row takes
 * ListTable's two layouts (LIST_TABLE_LAYOUT): below lg, "Total" on a line of its own, the two
 * totals under it with their headers above them, and the note on the last line.
 */

import {
  faCircleQuestion,
  faFlagUsa,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TaxProfileListRow } from '@shared/types/taxProfile';
import { formatPercent } from '@/pages/TaxProfile/format';
import { rowIsWaiting, shareTotal } from '@/pages/TaxProfile/listRows';
import type { RowDecisions } from '@/pages/TaxProfile/listRows';
import {
  EMPTY_CELL_DASH,
  LIST_EMPTY_SENTENCES,
  ROW_WORDS,
  TOTAL_REVIEWED_NOTE,
  TOTAL_WAITING_NOTE,
  US_PERSON_WORDS,
  shareTotalText,
} from '@/pages/TaxProfile/wording';
import ListTable, {
  LIST_TABLE_LAYOUT,
  ListCellContext,
  ListTableColumn,
  ListTableProps,
} from '@/pages/TaxProfile/components/ListTable';
import { BRAND_HEADING_CLASS } from '@/pages/TaxProfile/brand';

export const OWNERS_SECTION_ID = 'tax-profile-owners';

/**
 * The owner_foreign_person value as a yes or no: an approved value is a boolean and a waiting value
 * is its stored text, so both 'true' and true read as true. Null when the cell has neither.
 */
const foreignPersonOf = (value: ListCellContext['value']): boolean | null => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
};

/** The icon of the US person cell, the sentence of its tooltip and its opacity class. */
const usPersonIcon = (foreignPerson: boolean | null) => {
  if (foreignPerson === false)
    return {
      icon: faFlagUsa,
      text: US_PERSON_WORDS.domestic,
      className: 'opacity-70',
    };
  if (foreignPerson === true)
    return {
      icon: faGlobe,
      text: US_PERSON_WORDS.foreign,
      className: 'opacity-70',
    };
  return {
    icon: faCircleQuestion,
    text: US_PERSON_WORDS.unknown,
    className: 'opacity-50',
  };
};

function UsPersonIcon({ foreignPerson }: { foreignPerson: boolean | null }) {
  const { icon, text, className } = usPersonIcon(foreignPerson);
  return (
    <span
      className={`tooltip tooltip-left ${className}`}
      data-tip={text}
      role='img'
      aria-label={text}
    >
      <FontAwesomeIcon icon={icon} />
    </span>
  );
}

export const OWNER_COLUMNS: ListTableColumn[] = [
  { key: 'owner_full_legal_name', header: 'Name' },
  { key: 'owner_entity_type', header: 'Type', widthClass: 'w-[17%]' },
  {
    key: 'owner_ownership_pct',
    header: 'Owns',
    align: 'right',
    widthClass: 'w-[11%]',
  },
  {
    key: 'owner_profit_allocation_pct',
    header: 'Profit',
    align: 'right',
    widthClass: 'w-[11%]',
  },
  {
    key: 'owner_foreign_person',
    header: 'US person',
    widthClass: 'w-[11%]',
    text: ({ value }) => usPersonIcon(foreignPersonOf(value)).text,
    render: ({ value }) => (
      <UsPersonIcon foreignPerson={foreignPersonOf(value)} />
    ),
  },
];

/** The header of one of OWNER_COLUMNS, which the Total row draws above its totals below lg. */
const ownerHeader = (key: string): string =>
  OWNER_COLUMNS.find((column) => column.key === key)?.header ?? '';

/** One total of the Total row: "100%", or "98.5%, not 100%" in the warning color; a dash for none. */
function ShareTotalCell({
  owners,
  shareKey,
  header,
  decisionsOf,
}: {
  owners: TaxProfileListRow[];
  shareKey: string;
  /** The column's header, drawn above the total below lg, where the table header is hidden. */
  header: string;
  decisionsOf: (row: TaxProfileListRow) => RowDecisions;
}) {
  const sum = shareTotal(owners, shareKey, decisionsOf);
  return (
    <td
      className={`${LIST_TABLE_LAYOUT.cell} lg:text-right font-bold ${
        sum?.isOff ? 'text-warning' : ''
      }`}
      data-label={header}
      data-testid={`tax-profile-total-${shareKey}`}
    >
      {sum
        ? shareTotalText(formatPercent(sum.total), sum.isOff)
        : EMPTY_CELL_DASH}
    </td>
  );
}

export type OwnersTableProps = Omit<
  ListTableProps,
  'rows' | 'columns' | 'label' | 'nameKey' | 'noun' | 'renderFooter'
> & {
  owners: TaxProfileListRow[];
  /** Added to the section's own classes; TaxProfile.tsx passes the glow of the section in view. */
  className?: string;
};

export default function OwnersTable({
  owners,
  className = '',
  ...tableProps
}: OwnersTableProps) {
  const anyWaiting = owners.some(rowIsWaiting);
  return (
    <section id={OWNERS_SECTION_ID} className={`mb-6 scroll-mt-4 ${className}`}>
      <h3 className={`font-bold mb-1 ${BRAND_HEADING_CLASS}`}>Owners</h3>
      {owners.length === 0 ? (
        <p className='text-xs opacity-70'>{LIST_EMPTY_SENTENCES.owners}</p>
      ) : (
        <ListTable
          {...tableProps}
          rows={owners}
          columns={OWNER_COLUMNS}
          label='Owners'
          nameKey='owner_full_legal_name'
          noun='owner'
          personDetails
          renderFooter={(decisionsOf) => (
            <tr className={`${LIST_TABLE_LAYOUT.row} border-l-transparent`}>
              <td
                className={`${LIST_TABLE_LAYOUT.nameCell} font-bold border-l-transparent`}
              >
                {ROW_WORDS.total}
              </td>
              <td className={LIST_TABLE_LAYOUT.emptyCell} />
              <ShareTotalCell
                owners={owners}
                shareKey='owner_ownership_pct'
                header={ownerHeader('owner_ownership_pct')}
                decisionsOf={decisionsOf}
              />
              <ShareTotalCell
                owners={owners}
                shareKey='owner_profit_allocation_pct'
                header={ownerHeader('owner_profit_allocation_pct')}
                decisionsOf={decisionsOf}
              />
              <td className={LIST_TABLE_LAYOUT.emptyCell} />
              <td
                className={`${LIST_TABLE_LAYOUT.approvalCell} text-xs opacity-80 whitespace-normal`}
              >
                {anyWaiting ? TOTAL_WAITING_NOTE : TOTAL_REVIEWED_NOTE}
              </td>
            </tr>
          )}
        />
      )}
    </section>
  );
}
